import {
  NETWORK_DASHBOARD_COHORT_UNITS,
  NETWORK_DASHBOARD_STORE_AGE_FILTERS,
  type NetworkDashboardCohortUnit,
} from '@gozoki/api-types'
import { z } from 'zod'

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

const cohortLimits: Record<NetworkDashboardCohortUnit, { min: number; max: number }> = {
  day: { min: 1, max: 90 },
  week: { min: 1, max: 24 },
  month: { min: 1, max: 24 },
  year: { min: 1, max: 5 },
}

export const networkDashboardSearchSchema = z
  .object({
    storeIds: z.array(z.number()).optional().default([]),
    storeAgeFilter: z.enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS).optional().default('default'),
    ageStoreIds: z.array(z.number()).optional(),
    startDate: isoDate.optional(),
    endDate: isoDate.optional(),
    cohortUnit: z.enum(NETWORK_DASHBOARD_COHORT_UNITS).optional(),
    cohortValue: z.number().int().positive().optional(),
    comparableSelectionMode: z.enum(['auto', 'custom']).optional().default('auto'),
    comparableStoreIds: z.array(z.number()).optional().default([]),
    comparableStoreAgeFilter: z
      .enum(NETWORK_DASHBOARD_STORE_AGE_FILTERS)
      .optional()
      .default('default'),
    comparableAgeStoreIds: z.array(z.number()).optional(),
    comparableStartDate: isoDate.optional(),
    comparableEndDate: isoDate.optional(),
    comparableCohortUnit: z.enum(NETWORK_DASHBOARD_COHORT_UNITS).optional(),
    comparableCohortValue: z.number().int().positive().optional(),
  })
  .refine(
    (data) =>
      (data.startDate === undefined && data.endDate === undefined) ||
      (data.startDate !== undefined && data.endDate !== undefined),
    { message: 'startDate and endDate must both be set or both omitted', path: ['startDate'] },
  )
  .refine(
    (data) =>
      (data.cohortUnit === undefined && data.cohortValue === undefined) ||
      (data.cohortUnit !== undefined && data.cohortValue !== undefined),
    {
      message: 'cohortUnit and cohortValue must both be set or both omitted',
      path: ['cohortUnit'],
    },
  )
  .refine((data) => data.cohortUnit === undefined || data.startDate === undefined, {
    message: 'cohort params cannot be combined with date params',
    path: ['cohortUnit'],
  })
  .refine(
    (data) =>
      (data.comparableStartDate === undefined && data.comparableEndDate === undefined) ||
      (data.comparableStartDate !== undefined && data.comparableEndDate !== undefined),
    {
      message: 'comparableStartDate and comparableEndDate must both be set or both omitted',
      path: ['comparableStartDate'],
    },
  )
  .refine(
    (data) =>
      data.comparableSelectionMode === 'auto' ||
      (data.comparableStartDate !== undefined && data.comparableEndDate !== undefined) ||
      (data.comparableCohortUnit !== undefined && data.comparableCohortValue !== undefined),
    {
      message:
        'comparable date or cohort params are required when comparableSelectionMode is custom',
      path: ['comparableSelectionMode'],
    },
  )
  .refine(
    (data) =>
      (data.comparableCohortUnit === undefined && data.comparableCohortValue === undefined) ||
      (data.comparableCohortUnit !== undefined && data.comparableCohortValue !== undefined),
    {
      message: 'comparableCohortUnit and comparableCohortValue must both be set or both omitted',
      path: ['comparableCohortUnit'],
    },
  )
  .refine(
    (data) => data.comparableCohortUnit === undefined || data.comparableStartDate === undefined,
    {
      message: 'comparable cohort params cannot be combined with comparable date params',
      path: ['comparableCohortUnit'],
    },
  )
  .refine(
    (data) => {
      if (data.cohortUnit === undefined || data.cohortValue === undefined) {
        return true
      }
      const limit = cohortLimits[data.cohortUnit]
      return data.cohortValue >= limit.min && data.cohortValue <= limit.max
    },
    { message: 'cohortValue is out of range for cohortUnit', path: ['cohortValue'] },
  )
  .refine(
    (data) => {
      if (data.comparableCohortUnit === undefined || data.comparableCohortValue === undefined) {
        return true
      }
      const limit = cohortLimits[data.comparableCohortUnit]
      return data.comparableCohortValue >= limit.min && data.comparableCohortValue <= limit.max
    },
    {
      message: 'comparableCohortValue is out of range for comparableCohortUnit',
      path: ['comparableCohortValue'],
    },
  )

export type NetworkDashboardSearch = z.infer<typeof networkDashboardSearchSchema>
